/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/no-empty-function */
import { FunctionalComponent, createContext, h } from 'preact';
import { ScrollToId, getCurrentUiCategoriesChoices } from '../../App.helpers';
import { StateUpdater, useContext, useState } from 'preact/hooks';
import { TAB_VIEW, VIEW } from '../../enums';
import Usercentrics, {
  FIRST_LAYER_VARIANT,
  SECOND_LAYER_VARIANT,
  UI_VARIANT,
  isPrivacyButtonUI,
} from '@usercentrics/cmp-browser-sdk';
import { convertUiViewToSdkLayer } from '../../helpers';
import { useCategoryContext } from '../CategoryContext';
import { useSettingsContext } from '../SettingsContext';
import { useTcfContext } from '../TcfContext';

export interface UiData {
  abTestVariant: string;
  baseHref: string;
  clearCategoryPurposeScrollToId: () => void;
  clearServiceVendorScrollToId: () => void;
  closeView: () => void;
  firstLayerVariant: FIRST_LAYER_VARIANT | null;
  footerHeight: number;
  isAmpEnabled: boolean;
  isInFullScreen: boolean;
  isTrackingEnabled: boolean;
  currentLanguage: string;
  previousLanguage: string;
  previousView: VIEW;
  scrollToIdTabViewCategoryPurpose: ScrollToId;
  scrollToIdTabViewServiceVendors: ScrollToId;
  scrollToSubServices: boolean;
  secondLayerVariant: SECOND_LAYER_VARIANT | null;
  setAbTestVariant: StateUpdater<string>;
  setBaseHref: StateUpdater<string>;
  setFirstLayerVariant: StateUpdater<FIRST_LAYER_VARIANT | null>;
  setFooterHeight: StateUpdater<number>;
  setIsAmpEnabled: StateUpdater<boolean>;
  setIsInFullScreen: StateUpdater<boolean>;
  setLanguage: (language: string) => void;
  setScrollToIdTabViewCategoryPurpose: StateUpdater<ScrollToId>;
  setScrollToIdTabViewServiceVendor: StateUpdater<ScrollToId>;
  setScrollToSubServices: StateUpdater<boolean>;
  setSecondLayerVariant: StateUpdater<SECOND_LAYER_VARIANT | null>;
  setTabView: StateUpdater<TAB_VIEW>;
  settingsCloseView: VIEW;
  setUiVariant: StateUpdater<UI_VARIANT | null>;
  setView: (view: VIEW) => Promise<void>;
  tabView: TAB_VIEW;
  uiVariant: UI_VARIANT | null;
  updateView: (newView: VIEW) => Promise<void>;
  view: VIEW;
}

const initialValues: UiData = {
  abTestVariant: '',
  baseHref: '',
  clearCategoryPurposeScrollToId: () => {},
  clearServiceVendorScrollToId: () => {},
  closeView: () => {},
  currentLanguage: '',
  firstLayerVariant: null,
  footerHeight: 0,
  isAmpEnabled: false,
  isInFullScreen: false,
  isTrackingEnabled: true,
  previousLanguage: '',
  previousView: VIEW.NONE,
  scrollToIdTabViewCategoryPurpose: '',
  scrollToIdTabViewServiceVendors: '',
  scrollToSubServices: false,
  secondLayerVariant: null,
  setAbTestVariant: () => {},
  setBaseHref: () => {},
  setFirstLayerVariant: () => {},
  setFooterHeight: () => {},
  setIsAmpEnabled: () => {},
  setIsInFullScreen: () => {},
  setLanguage: () => {},
  setScrollToIdTabViewCategoryPurpose: () => {},
  setScrollToIdTabViewServiceVendor: () => {},
  setScrollToSubServices: () => {},
  setSecondLayerVariant: () => {},
  setTabView: () => {},
  settingsCloseView: VIEW.NONE,
  setUiVariant: () => {},
  setView: () => Promise.resolve(),
  tabView: TAB_VIEW.CATEGORIES_PURPOSES,
  uiVariant: UI_VARIANT.DEFAULT,
  updateView: () => Promise.resolve(),
  view: VIEW.NONE,
};

const UiContext = createContext<UiData>(initialValues);

type FirstLayerVariantType = FIRST_LAYER_VARIANT | null;
type SecondLayerVariantType = SECOND_LAYER_VARIANT | null;
type UiVariantType = UI_VARIANT | null;

interface Props {
  disableTracking: boolean;
  UC: Usercentrics;
}

export const UiContextProvider: FunctionalComponent<Props> = ({ children, disableTracking, UC }) => {
  const [abTestVariant, setAbTestVariant] = useState(initialValues.abTestVariant);
  const [baseHref, setBaseHref] = useState(initialValues.baseHref);
  const [isAmpEnabled, setIsAmpEnabled] = useState(initialValues.isAmpEnabled);
  const [uiVariant, setUiVariant] = useState<UiVariantType>(null);
  const [firstLayerVariant, setFirstLayerVariant] = useState<FirstLayerVariantType>(null);
  const [scrollToIdTabViewCategoryPurpose, setScrollToIdTabViewCategoryPurpose] = useState<ScrollToId>(
    initialValues.scrollToIdTabViewCategoryPurpose,
  );
  const [scrollToIdTabViewServiceVendors, setScrollToIdTabViewServiceVendor] = useState<ScrollToId>(
    initialValues.scrollToIdTabViewServiceVendors,
  );
  const [scrollToSubServices, setScrollToSubServices] = useState(initialValues.scrollToSubServices);
  const [secondLayerVariant, setSecondLayerVariant] = useState<SecondLayerVariantType>(null);
  const [tabView, setTabView] = useState(initialValues.tabView);
  const [viewState, setViewState] = useState({ current: initialValues.view, previous: initialValues.previousView });
  const [settingsCloseView, setSettingsCloseView] = useState(initialValues.settingsCloseView);
  const [isInFullScreen, setIsInFullScreen] = useState(initialValues.isInFullScreen);
  const [footerHeight, setFooterHeight] = useState(initialValues.footerHeight);
  const [languageState, setLanguageState] = useState({
    currentLanguage: initialValues.currentLanguage,
    previousLanguage: initialValues.previousLanguage,
  });

  const { categories, getUpdatedCategories, setCategories } = useCategoryContext();
  const { setTcfData } = useTcfContext();
  const { ui, setSettingsLabels, setSettingsUI } = useSettingsContext();

  const setView = async (view: VIEW): Promise<void> => {
    await UC.updateLayer(convertUiViewToSdkLayer(view)).then(async () => {
      const newCat = await getUpdatedCategories(view);
      setAbTestVariant(UC.getAbTestVariant());
      setCategories(getCurrentUiCategoriesChoices(newCat, categories));
      setSettingsLabels(UC.getSettingsLabels());
      setSettingsUI(UC.getSettingsUI());

      // TODO (EUD-344) keep yet unsaved toggle states for TCF, similar to the categories logic above.
      if (uiVariant === UI_VARIANT.TCF) {
        setTcfData(UC.getTCFData());
      }
    });

    if (view !== VIEW.SECOND_LAYER) {
      setSettingsCloseView(view);
    }

    setViewState((prevViewState) => ({
      current: view,
      previous: prevViewState.current,
    }));
  };

  const setLanguage = (lang: string) => {
    setLanguageState((prevLang) => ({
      currentLanguage: lang,
      previousLanguage: prevLang.currentLanguage,
    }));
  };

  const clearCategoryPurposeScrollToId = () => {
    setScrollToIdTabViewCategoryPurpose('');
  };

  const clearServiceVendorScrollToId = () => {
    setScrollToIdTabViewServiceVendor('');
  };

  const updateView = async (newView: VIEW): Promise<void> => {
    await setView(newView);

    setSettingsLabels(UC.getSettingsLabels());
    setSettingsUI(UC.getSettingsUI());
    setCategories(await getUpdatedCategories(newView));

    if (uiVariant === UI_VARIANT.TCF) {
      setTcfData(UC.getTCFData());
    }
  };

  const closeView = (): void => {
    if (ui && !isPrivacyButtonUI(ui) && ui.enablePrivacyButton) {
      setView(VIEW.PRIVACY_BUTTON);
    } else {
      setView(VIEW.NONE);
    }
  };

  return (
    <UiContext.Provider
      value={{
        abTestVariant,
        baseHref,
        clearCategoryPurposeScrollToId,
        clearServiceVendorScrollToId,
        closeView,
        currentLanguage: languageState.currentLanguage,
        firstLayerVariant,
        footerHeight,
        isAmpEnabled,
        isInFullScreen,
        isTrackingEnabled: !disableTracking,
        previousLanguage: languageState.previousLanguage,
        previousView: viewState.previous,
        scrollToIdTabViewCategoryPurpose,
        scrollToIdTabViewServiceVendors,
        scrollToSubServices,
        secondLayerVariant,
        setAbTestVariant,
        setBaseHref,
        setFirstLayerVariant,
        setFooterHeight,
        setIsAmpEnabled,
        setIsInFullScreen,
        setLanguage,
        setScrollToIdTabViewCategoryPurpose,
        setScrollToIdTabViewServiceVendor,
        setScrollToSubServices,
        setSecondLayerVariant,
        setTabView,
        settingsCloseView,
        setUiVariant,
        setView,
        tabView,
        uiVariant,
        updateView,
        view: viewState.current,
      }}
    >
      {children}
    </UiContext.Provider>
  );
};

export const useUiContext = (): UiData => {
  return useContext(UiContext);
};
